import Button from "@/components/Button"
import { CopyText } from "@/components/CopyText"
import DevErrorToast from "@/components/DevErrorToast"
import Dialog from "@/components/Dialog"
import InputLabel from "@/components/InputLabel"
import Page from "@/components/Page"
import PingIndicator from "@/components/PingIndicator"
import { useSectionContext } from "@/components/sidebar/SectionContext"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import Tooltip from "@/components/Tooltip"
import {
  useAddCSFlagMutation,
  usePatientDetailPageDataQuery,
  useRemoveCSFlagMutation,
  useSendPatientInvitationMutation,
  useSendResetPasswordEmailMutation,
} from "@/data/queries"
import { getDeviceOrderProgram } from "@/model/CustomerSupport"
import { DeviceOrder, deviceOrderStatusLabel } from "@/model/DeviceOrders"
import { LoginState } from "@/model/Patient"
import { Pregnancy } from "@/model/Pregnancy"
import {
  ClinicalProtocolType,
  DiabetesPrescription,
  HypertensionPrescription,
  Prescription,
} from "@/model/Prescription"
import Badge from "@/pages/customer-support/components/Badge"
import CSFlagButton from "@/pages/customer-support/components/CSFlagButton"
import CSFlagDialog from "@/pages/customer-support/components/CSFlagDialog"
import CustomerSupportNotes from "@/pages/patient-details/CustomerSupportNotes"
import DeviceForm from "@/pages/patient-details/devices/DeviceForm"
import DeviceList from "@/pages/patient-details/devices/DeviceList"
import EditPatientDialog from "@/pages/patient-details/edit-patient-dialog/EditPatientDialog"
import LabeledText from "@/pages/patient-details/LabeledText"
import PregnancyInfo from "@/pages/patient-details/pregnancy/PregnancyInfo"
import DiabetesPrescriptionDialog from "@/pages/patient-details/prescription/DiabetesPrescriptionDialog"
import DiabetesPrescriptionInfo from "@/pages/patient-details/prescription/DiabetesPrescriptionInfo"
import HypertensionPrescriptionDialog from "@/pages/patient-details/prescription/HypertensionPrescriptionDialog"
import HypertensionPrescriptionInfo from "@/pages/patient-details/prescription/HypertensionPrescriptionInfo"
import ReminderForm from "@/pages/patient-details/reminders/ReminderForm"
import RemindersList from "@/pages/patient-details/reminders/RemindersList"
import ReminderStatusBadge from "@/pages/patient-details/reminders/ReminderStatusBadge"
import cn from "@/utils/cn"
import { formatDate } from "@/utils/date-utils"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { ChronoUnit, Instant } from "@js-joda/core"
import { Fragment, useState } from "react"
import { useParams } from "react-router"
import { toast } from "sonner"
import { formatPhoneNumber } from "./utils"

type PrescriptionModalState = {
  type: ClinicalProtocolType
}

const PatientDetailPage = () => {
  const { currentSection, currentSectionRootPath, goBackToSectionRoot } =
    useSectionContext()
  const { bloomlifeId: bloomlifeIdFromParams } = useParams()
  const bloomlifeId = bloomlifeIdFromParams!
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false)
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false)
  const [isPatientInfoModalOpen, setIsPatientInfoModalOpen] = useState(false)
  const [prescriptionModalState, setPrescriptionModalState] =
    useState<PrescriptionModalState | null>(null)
  const currentlyOpenPrescriptionModal = prescriptionModalState?.type
  const [isCSFlagDialogOpen, setIsCSFlagDialogOpen] = useState(false)

  const {
    mutateAsync: sendPatientInvitation,
    isPending: isSendingPatientInvitation,
  } = useSendPatientInvitationMutation(bloomlifeId)

  const {
    mutateAsync: sendResetPasswordEmail,
    isPending: isSendingResetPasswordEmail,
  } = useSendResetPasswordEmailMutation()

  const {
    data: pageData,
    isPending: isLoadingPageData,
    isError: isErrorPageData,
  } = usePatientDetailPageDataQuery(bloomlifeId)

  const { mutateAsync: addCSFlag, isPending: isAddingCSFlag } =
    useAddCSFlagMutation(bloomlifeId)
  const { mutateAsync: removeCSFlag, isPending: isRemovingCSFlag } =
    useRemoveCSFlagMutation(bloomlifeId)

  const [selectedPregnancyId, setSelectedPregnancyId] = useState<string | null>(
    null
  )

  const onAddDiabetesPrescriptionSelected = () => {
    if (pageData!.patient.activeDiabetesPrescriptionId) {
      toast.custom((toastId) => (
        <Toast
          description="The patient already has an active diabetes prescription."
          toastId={toastId}
          variant="error"
        />
      ))
    } else {
      setPrescriptionModalState({
        type: "diabetes",
      })
    }
  }
  const onAddHypertensionPrescriptionSelected = () => {
    if (pageData!.patient.activeHypertensionPrescriptionId) {
      toast.custom((toastId) => (
        <Toast
          description="The patient already has an active hypertension prescription."
          toastId={toastId}
          variant="error"
        />
      ))
    } else {
      setPrescriptionModalState({
        type: "hypertension",
      })
    }
  }
  const closePrescriptionModal = () => {
    setPrescriptionModalState(null)
  }

  const openDeviceModal = () => setIsDeviceModalOpen(true)
  const closeDeviceModal = () => setIsDeviceModalOpen(false)

  const openReminderModal = () => setIsReminderModalOpen(true)
  const closeReminderModal = () => setIsReminderModalOpen(false)

  const openPatientInfoModal = () => setIsPatientInfoModalOpen(true)
  const closePatientInfoModal = () => setIsPatientInfoModalOpen(false)

  const handleOpenCSFlagDialog = () => {
    setIsCSFlagDialogOpen(true)
  }

  const handleCSFlagSave = async (reason: string) => {
    try {
      await addCSFlag({ bloomlifeId, reason })
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="CS flag saved successfully." />
      ))
      setIsCSFlagDialogOpen(false)
    } catch (error) {
      toast.custom(
        (toastId) => (
          <DevErrorToast
            toastId={toastId}
            userMessage="Something went wrong while saving the CS flag. Try again, or contact the dev team."
            error={error}
          />
        ),
        { duration: Infinity }
      )
    }
  }

  const handleCSFlagRemove = async () => {
    try {
      await removeCSFlag({ bloomlifeId })
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="CS flag removed successfully." />
      ))
      setIsCSFlagDialogOpen(false)
    } catch (error) {
      toast.custom(
        (toastId) => (
          <DevErrorToast
            toastId={toastId}
            userMessage="Something went wrong while removing the CS flag. Try again, or contact the dev team."
            error={error}
          />
        ),
        { duration: Infinity }
      )
    }
  }

  if (isLoadingPageData) {
    return (
      <Page>
        <Spinner className="mt-4" />
      </Page>
    )
  }

  if (isErrorPageData) {
    return (
      <Page>
        <p className="mt-4 text-center text-red-500 italic">
          Something went wrong while loading the patient, please check the URL
          and try again.
        </p>
      </Page>
    )
  }

  const {
    patient,
    organization,
    prescriptions = [],
    pregnancies = [],
    devices = [],
    reminders = [],
  } = pageData
  const caregivers = organization?.caregivers ?? []
  const departments = organization?.departments ?? []

  const deviceOrders: DeviceOrder[] = prescriptions
    .flatMap((prescription) => prescription.deviceOrders)
    .sort((a, b) => b.orderCreatedAt.compareTo(a.orderCreatedAt))

  const preferredLocale = organization?.supportedLocales?.find(
    ({ locale }) => locale === patient.preferredLocale
  )?.name

  const activePregnancyId =
    prescriptions.find(
      ({ id }) =>
        id === patient.activeHypertensionPrescriptionId ||
        id === patient.activeDiabetesPrescriptionId
    )?.pregnancyId ??
    pregnancies.find(({ status }) => status === "monitoring")?.id ??
    pregnancies[pregnancies.length - 1]?.id

  const selectedPregnancy =
    pregnancies.find(
      ({ id }) => id === (selectedPregnancyId ?? activePregnancyId)
    ) ?? pregnancies[0]

  const prescriptionsForSelectedPregnancy = selectedPregnancy
    ? prescriptions.filter(
        ({ pregnancyId }) => pregnancyId === selectedPregnancy.id
      )
    : prescriptions

  const hasActivePrescription = prescriptions.some(
    ({ status }) => status === "active"
  )

  const activeHypertensionPrescription = prescriptionsForSelectedPregnancy.find(
    ({ id }) => id === patient.activeHypertensionPrescriptionId
  ) as HypertensionPrescription | undefined

  const activeDiabetesPrescription = prescriptionsForSelectedPregnancy.find(
    ({ id }) => id === patient.activeDiabetesPrescriptionId
  ) as DiabetesPrescription | undefined

  const inactiveHypertensionPrescriptions =
    prescriptionsForSelectedPregnancy.filter(
      ({ status, type }) => status === "inactive" && type === "hypertension"
    ) as HypertensionPrescription[]

  const inactiveDiabetesPrescriptions =
    prescriptionsForSelectedPregnancy.filter(
      ({ status, type }) => status === "inactive" && type === "diabetes"
    ) as DiabetesPrescription[]

  const invitePatient = async () => {
    if (!patient.emailAddress) {
      toast.custom((toastId) => (
        <Toast
          description="The patient does not have an email address. Please add one."
          toastId={toastId}
          variant="error"
        />
      ))
      return
    }
    try {
      await sendPatientInvitation(bloomlifeId)
      toast.custom((toastId) => (
        <Toast description="Patient invited successfully." toastId={toastId} />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Failed to invite the patient. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  const sendResetPasswordEmailToPatient = async () => {
    if (!patient.emailAddress) {
      toast.custom((toastId) => (
        <Toast
          description="The patient does not have an email address. Please add one."
          toastId={toastId}
          variant="error"
        />
      ))
      return
    }
    try {
      await sendResetPasswordEmail(patient.emailAddress)
      toast.custom((toastId) => (
        <Toast
          description="An email has been sent to the patient to reset their password."
          toastId={toastId}
        />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Failed to send reset password email. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  const breadcrumbs = [
    {
      element:
        currentSection === "population"
          ? "Patient overview"
          : "Customer support",
      href: currentSectionRootPath,
    },
    { element: organization.longName ?? "-" },
    { element: patient.medicalRecordNumber ?? "-" },
  ]

  const actionButtons = () => {
    const isPending = isAddingCSFlag || isRemovingCSFlag

    return (
      <div className="flex items-center gap-4">
        <CSFlagButton
          isFlagged={patient.customerSupportNotes?.flagged ?? false}
          csFlagNotes={patient.customerSupportNotes?.reason}
          onClick={handleOpenCSFlagDialog}
          isPending={isPending}
          showTooltip={false}
        />
        {inactiveDiabetesPrescriptions.length !== 0 ||
        activeDiabetesPrescription ? (
          <>
            {patient.loginState === "active" ? (
              <Button
                variant="primary"
                disabled={isSendingResetPasswordEmail}
                className="flex items-center gap-2 px-4"
                onClick={sendResetPasswordEmailToPatient}
              >
                {isSendingResetPasswordEmail && <Spinner className="h-4 w-4" />}
                Reset password
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={isSendingPatientInvitation}
                className="flex items-center gap-2 px-4"
                onClick={invitePatient}
              >
                {isSendingPatientInvitation && <Spinner className="h-4 w-4" />}
                {patient.loginState === "no-access"
                  ? "Send invitation"
                  : "Resend invitation"}
              </Button>
            )}
          </>
        ) : null}
      </div>
    )
  }

  return (
    <Page breadcrumbs={breadcrumbs}>
      <div className="mx-auto mb-32 w-full max-w-[1100px]">
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span
              className="material-icons cursor-pointer rounded-md bg-primary/10 p-1 text-[28px] transition-colors hover:bg-primary/20"
              onClick={goBackToSectionRoot}
            >
              keyboard_arrow_left
            </span>
            <p className="text-4xl font-bold">
              {patient.lastName}, {patient.firstName}
            </p>
            <span
              className={cn(
                "mt-1 rounded-full px-4 py-1 text-sm font-bold tracking-wide text-primary",
                classForLoginStateCapsule(patient.loginState)
              )}
            >
              {titleForLoginState(patient.loginState)}
            </span>
          </div>
          <div className="flex items-center gap-4">{actionButtons()}</div>
        </div>

        {patient.customerSupportNotes?.flagged &&
          patient.customerSupportNotes.reason && (
            <div className="my-4 flex items-center gap-2 rounded-lg border border-red-500/5 bg-red-100 px-4 py-3 text-red-600">
              <span className="material-icons">flag</span>
              <p className="flex-1 text-sm font-medium">
                {patient.customerSupportNotes.reason}
              </p>
            </div>
          )}

        <div className="mt-8">
          <div className="flex items-center gap-2">
            <h4 className="text-2xl font-extrabold">Patient info</h4>
            <span
              className={cn(
                "material-icons-outlined cursor-pointer text-[26px] text-primary/60 transition-colors hover:text-primary",
                isSendingPatientInvitation || isSendingResetPasswordEmail
                  ? "pointer-events-none cursor-not-allowed opacity-50"
                  : ""
              )}
              onClick={openPatientInfoModal}
            >
              edit
            </span>
          </div>
          <div className="mt-4 flex flex-col rounded-xl border border-black/10 bg-white p-6 shadow-md shadow-black/5">
            <div className="grid grid-cols-4 gap-4">
              <LabeledText label="MRN" value={patient.medicalRecordNumber} />
              <LabeledText
                label="Bloomlife ID"
                value={patient.bloomlifeId}
                className="col-span-3"
              />

              <LabeledText label="First name" value={patient.firstName} />
              <LabeledText label="Last name" value={patient.lastName} />
              <LabeledText label="Email address" value={patient.emailAddress} />
              <LabeledText
                label="Phone number"
                value={formatPhoneNumber(patient.phoneNumber ?? "")}
              />

              <LabeledText
                label="Date of birth"
                value={
                  patient.dateOfBirth &&
                  formatDate(patient.dateOfBirth, "MM/dd/yyyy")
                }
              />
              <LabeledText label="Preferred language" value={preferredLocale} />
              <LabeledText label="Timezone" value={patient.timeZone} />
              <LabeledText
                label="Insurance provider"
                value={patient.insuranceProvider}
              />
            </div>
            <div className="mx-4 my-8 h-px w-[80%] self-center bg-gray-300" />
            <div className="mb-4 flex items-center gap-2">
              <h4 className="text-xl font-extrabold">Shipping address</h4>
              {patient.shippingAddressLastUpdatedBy && (
                <div className="flex items-center gap-1 text-sm text-primary/70">
                  <span className="flex items-center justify-center">•</span>
                  <span>
                    Last updated by {patient.shippingAddressLastUpdatedBy}
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-4 grid-rows-2 gap-4">
              <LabeledText
                label="Address 1"
                value={patient.shippingAddress?.address1}
              />

              <LabeledText
                label="Address 2"
                value={patient.shippingAddress?.address2}
              />
              <LabeledText label="City" value={patient.shippingAddress?.city} />
              <LabeledText
                label="State"
                value={patient.shippingAddress?.state}
              />
              <LabeledText
                label="Zip"
                value={patient.shippingAddress?.zipCode}
              />
              <LabeledText
                label="AT&T Compatibility"
                value={
                  <>
                    {patient.isAttCompatible !== undefined && (
                      <Badge
                        label={
                          patient.isAttCompatible
                            ? "Compatible"
                            : "Not Compatible"
                        }
                        icon={patient.isAttCompatible ? "check" : "close"}
                        variant={patient.isAttCompatible ? "success" : "danger"}
                      />
                    )}
                    {patient.isAttCompatible === undefined && "-"}
                  </>
                }
              />
            </div>
          </div>
        </div>

        <div className="mt-12 flex items-center gap-3">
          <h4 className="text-2xl font-extrabold">Devices</h4>
          <span
            className="material-icons mt-1 cursor-pointer text-[26px] text-primary/60 transition-colors hover:text-primary"
            onClick={openDeviceModal}
          >
            add_circle_outline
          </span>
        </div>
        <div className="mt-4 rounded-xl border border-black/10 bg-white p-6 shadow-md shadow-black/5">
          <DeviceList
            devices={devices}
            bloomlifeId={patient.bloomlifeId}
            timeZone={patient.timeZone}
          />
        </div>

        <div className="my-4">
          <h4 className="text-2xl font-extrabold">Orders</h4>
        </div>

        <div className="mt-4 rounded-xl border border-black/10 bg-white p-6 shadow-md shadow-black/5">
          {deviceOrders.length > 0 ? (
            <div>
              <div className="grid grid-cols-7 gap-4">
                <InputLabel className="font-bold">Type</InputLabel>
                <InputLabel className="font-bold">Vendor</InputLabel>
                <InputLabel className="font-bold">Order ID</InputLabel>
                <InputLabel className="font-bold">Order Number</InputLabel>
                <InputLabel className="font-bold">Tracking Number</InputLabel>
                <InputLabel className="font-bold">
                  Days Since Order Placed
                </InputLabel>
                <InputLabel className="font-bold">Tracking Status</InputLabel>
              </div>

              {deviceOrders.map((deviceOrder) => (
                <div
                  key={deviceOrder.orderNumber}
                  className="mt-3 grid grid-cols-7 items-center gap-4"
                >
                  <p>{getDeviceOrderProgram(deviceOrder.type)}</p>
                  <p>{deviceOrder.vendor}</p>
                  <CopyText
                    text={deviceOrder.vendorOrderId || ""}
                    label="Order ID"
                  >
                    {deviceOrder.vendorOrderId || "-"}
                  </CopyText>
                  <p>{deviceOrder.orderNumber}</p>
                  <p>
                    {(deviceOrder.carrier || "") +
                      " " +
                      (deviceOrder.trackingNumber || "-")}
                  </p>
                  <p>
                    {`${deviceOrder.orderCreatedAt.until(Instant.now(), ChronoUnit.DAYS)} days`}
                  </p>
                  <p>{deviceOrderStatusLabel(deviceOrder.status)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-primary/70 italic">No orders found</p>
          )}
        </div>

        {/* Reminders Section: always show, with capsule and explanation */}
        <div className="mt-12">
          <div className="flex items-center gap-3">
            <h4 className="text-2xl font-extrabold">Reminders</h4>
            <ReminderStatusBadge
              active={!!patient.activeHypertensionPrescriptionId}
            />
            <span
              className="material-icons mt-0.5 cursor-pointer text-[28px] text-primary/60 transition-colors hover:text-primary"
              onClick={openReminderModal}
            >
              add_circle_outline
            </span>
          </div>
          <div className="mt-4 rounded-xl border border-black/10 bg-white p-6 shadow-md shadow-black/5">
            <RemindersList
              reminders={reminders}
              bloomlifeId={patient.bloomlifeId}
            />
          </div>
        </div>

        <div className="mt-12 flex flex-col">
          <div className="flex items-center">
            <h4 className="text-2xl font-extrabold">Prescriptions</h4>
            {selectedPregnancy && (
              <PregnancyStatusBadge
                pregnancy={selectedPregnancy}
                prescriptions={prescriptions}
              />
            )}
            <Popover className="relative ms-4">
              <PopoverButton className="flex cursor-pointer items-center focus-visible:outline-hidden">
                <span className="material-icons cursor-pointer text-[28px] text-primary/60 transition-colors hover:text-primary">
                  add_circle_outline
                </span>
              </PopoverButton>
              <PopoverPanel
                anchor="bottom start"
                className="flex w-40 flex-col rounded-lg border border-primary/20 bg-white"
              >
                <li
                  className="block cursor-pointer rounded-lg px-4 py-2 text-[16px] transition hover:bg-primary/5"
                  onClick={onAddDiabetesPrescriptionSelected}
                >
                  Diabetes
                </li>
                <li
                  className="block cursor-pointer rounded-lg px-4 py-2 text-[16px] transition hover:bg-primary/5"
                  onClick={onAddHypertensionPrescriptionSelected}
                >
                  Hypertension
                </li>
              </PopoverPanel>
            </Popover>
            {selectedPregnancy && pregnancies.length > 1 && (
              <div className="ms-8 flex items-center">
                {pregnancies.map((pregnancy, index) => {
                  const isSelected = pregnancy.id === selectedPregnancy.id
                  const dotClasses = cn(
                    "size-[10px] cursor-pointer rounded-full bg-slate-500 transition-transform ease-out hover:scale-[1.35]",
                    isSelected
                      ? "scale-[1.75] hover:scale-[1.75]"
                      : "opacity-50"
                  )
                  return (
                    <Fragment key={pregnancy.id}>
                      {index > 0 && <div className="h-0.5 w-12 bg-slate-300" />}
                      <Tooltip
                        content={
                          !isSelected
                            ? tooltipForPregnancy(pregnancy, prescriptions)
                            : null
                        }
                      >
                        {pregnancy.id === activePregnancyId ? (
                          <PingIndicator
                            className={dotClasses}
                            onClick={() => setSelectedPregnancyId(pregnancy.id)}
                          />
                        ) : (
                          <div
                            className={dotClasses}
                            onClick={() => setSelectedPregnancyId(pregnancy.id)}
                          />
                        )}
                      </Tooltip>
                    </Fragment>
                  )
                })}
              </div>
            )}
          </div>
          {(selectedPregnancy || prescriptions.length > 0) && (
            <div className="mt-4 flex gap-4 rounded-2xl bg-gray-100 p-4 shadow-inner shadow-black/15">
              <div className="flex flex-1 flex-col gap-4">
                {selectedPregnancy && (
                  <PregnancyInfo
                    bloomlifeId={patient.bloomlifeId}
                    pregnancy={selectedPregnancy}
                  />
                )}
                {activeHypertensionPrescription && (
                  <HypertensionPrescriptionInfo
                    organization={organization}
                    bloomlifeId={patient.bloomlifeId}
                    departments={departments}
                    caregivers={caregivers}
                    prescription={activeHypertensionPrescription}
                    patientName={`${patient.lastName}, ${patient.firstName}`}
                    attCompatibility={patient.isAttCompatible}
                    shippingAddress={patient.shippingAddress}
                  />
                )}

                {activeDiabetesPrescription && (
                  <DiabetesPrescriptionInfo
                    organization={organization}
                    bloomlifeId={patient.bloomlifeId}
                    caregivers={caregivers}
                    departments={departments}
                    prescription={activeDiabetesPrescription}
                    patientName={`${patient.lastName}, ${patient.firstName}`}
                    attCompatibility={patient.isAttCompatible}
                    shippingAddress={patient.shippingAddress}
                  />
                )}
                {inactiveHypertensionPrescriptions.map((prescription) => (
                  <HypertensionPrescriptionInfo
                    organization={organization}
                    bloomlifeId={patient.bloomlifeId}
                    departments={departments}
                    caregivers={caregivers}
                    prescription={prescription}
                    patientName={`${patient.lastName}, ${patient.firstName}`}
                    attCompatibility={patient.isAttCompatible}
                    shippingAddress={patient.shippingAddress}
                  />
                ))}
                {inactiveDiabetesPrescriptions.map((prescription) => (
                  <DiabetesPrescriptionInfo
                    organization={organization}
                    bloomlifeId={patient.bloomlifeId}
                    departments={departments}
                    caregivers={caregivers}
                    prescription={prescription}
                    patientName={`${patient.lastName}, ${patient.firstName}`}
                    attCompatibility={patient.isAttCompatible}
                    shippingAddress={patient.shippingAddress}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <DiabetesPrescriptionDialog
        isOpen={currentlyOpenPrescriptionModal === "diabetes"}
        onClose={closePrescriptionModal}
        organization={organization}
        bloomlifeId={patient.bloomlifeId}
        pregnancies={pregnancies}
        defaultPregnancyId={selectedPregnancy?.id}
        hasActivePrescription={hasActivePrescription}
        caregivers={caregivers}
        departments={departments}
      />

      <HypertensionPrescriptionDialog
        isOpen={currentlyOpenPrescriptionModal === "hypertension"}
        onClose={closePrescriptionModal}
        organization={organization}
        bloomlifeId={patient.bloomlifeId}
        pregnancies={pregnancies}
        defaultPregnancyId={selectedPregnancy?.id}
        hasActivePrescription={hasActivePrescription}
        caregivers={caregivers}
        departments={departments}
      />

      <Dialog
        title="Add device"
        isOpen={isDeviceModalOpen}
        onClose={closeDeviceModal}
      >
        <DeviceForm
          bloomlifeId={patient.bloomlifeId}
          onCancel={closeDeviceModal}
        />
      </Dialog>

      <Dialog
        title="Add reminder"
        isOpen={isReminderModalOpen}
        onClose={closeReminderModal}
        headerRight={
          <ReminderStatusBadge
            active={!!patient.activeHypertensionPrescriptionId}
          />
        }
      >
        <ReminderForm
          onCancel={closeReminderModal}
          patientReminders={reminders ?? []}
          bloomlifeId={patient.bloomlifeId}
        />
      </Dialog>

      <EditPatientDialog
        patient={patient}
        patientOrganization={organization}
        isOpen={isPatientInfoModalOpen}
        onClose={closePatientInfoModal}
      />

      <CSFlagDialog
        isOpen={isCSFlagDialogOpen}
        onClose={() => setIsCSFlagDialogOpen(false)}
        csFlag={patient.customerSupportNotes?.flagged ?? false}
        currentReason={patient.customerSupportNotes?.reason}
        onSave={handleCSFlagSave}
        onRemove={handleCSFlagRemove}
        isSaving={isAddingCSFlag}
        isRemoving={isRemovingCSFlag}
      />

      <CustomerSupportNotes bloomlifeId={patient.bloomlifeId} />
    </Page>
  )
}

export default PatientDetailPage

const titleForLoginState = (loginState: LoginState) => {
  switch (loginState) {
    case "no-access":
      return "Disabled"
    case "invited":
      return "Invited"
    case "active":
      return "Active"
  }
}

const classForLoginStateCapsule = (loginState: LoginState) => {
  switch (loginState) {
    case "no-access":
      return "border border-gray-300 bg-gray-200 text-primary/50"
    case "invited":
      return "border border-yellow-300 bg-yellow-200 text-yellow-700"
    case "active":
      return "border border-green-300 bg-green-200 text-green-700"
  }
}

const PregnancyStatusBadge = ({
  pregnancy,
  prescriptions,
}: {
  pregnancy: Pregnancy
  prescriptions: Prescription[]
}) => {
  const isDelivered = pregnancy.status === "delivered"
  const hasActivePrescription = prescriptions.some(
    (prescription) =>
      prescription.pregnancyId === pregnancy.id &&
      prescription.status === "active"
  )
  const label = isDelivered
    ? "Delivered"
    : hasActivePrescription
      ? "Monitoring"
      : "Inactive"
  const isHighlighted = !isDelivered && hasActivePrescription
  return (
    <span
      className={cn(
        "ms-4 cursor-default rounded-full border px-3 py-1 text-xs font-semibold",
        isHighlighted
          ? "border-blue-200 bg-blue-100 text-blue-700"
          : "border-gray-200 bg-gray-100 text-gray-600"
      )}
    >
      {label}
    </span>
  )
}

const tooltipForPregnancy = (
  pregnancy: Pregnancy,
  prescriptions: Prescription[]
): string => {
  if (pregnancy.status === "delivered" && pregnancy.deliveryDate) {
    return `Delivered ${formatDate(pregnancy.deliveryDate, "MM/dd/yyyy")}`
  }
  if (pregnancy.estimatedDateOfDelivery) {
    return `EDD ${formatDate(pregnancy.estimatedDateOfDelivery, "MM/dd/yyyy")}`
  }
  const earliestStart = prescriptions
    .filter((prescription) => prescription.pregnancyId === pregnancy.id)
    .map((prescription) => prescription.startDate)
    .sort((a, b) => a.compareTo(b))[0]
  if (earliestStart) {
    return `Monitoring since ${formatDate(earliestStart, "MM/dd/yyyy")}`
  }
  return "Monitoring"
}
